import { useEffect, useLayoutEffect, useRef, useState } from 'react'

const DRAG_SENSITIVITY_PX = 30
const SLIDE_TRANSITION = 'transform 0.3s ease'
const WHEEL_LOCK_MS = 300

type DragSession = {
  active: boolean
  pointerId: number | null
  startX: number
}

const idleDragSession = (): DragSession => ({
  active: false,
  pointerId: null,
  startX: 0,
})

const readSlideStep = (track: HTMLElement | null) => {
  const slide = track?.firstElementChild as HTMLElement | null

  if (!track || !slide) {
    return 0
  }

  const parsedGap = Number.parseFloat(getComputedStyle(track).columnGap)
  const gap = Number.isFinite(parsedGap) ? parsedGap : 0
  const width = slide.offsetWidth

  return (Number.isFinite(width) ? width : 0) + gap
}

type TrackOffset = {
  currentIndex: number
  slideStep: number
  maxIndex: number
  dragOffset: number
  isDragging: boolean
}

const getTrackOffset = ({
  currentIndex,
  slideStep,
  maxIndex,
  dragOffset,
  isDragging,
}: TrackOffset) => {
  const baseOffset = -currentIndex * slideStep
  const minOffset = -maxIndex * slideStep
  const rawOffset = baseOffset + (isDragging ? dragOffset : 0)

  return Math.min(0, Math.max(minOffset, rawOffset))
}

const capturePointer = (element: HTMLDivElement, pointerId: number) => {
  if (typeof element.setPointerCapture !== 'function') {
    return
  }

  try {
    element.setPointerCapture(pointerId)
  } catch {
    // Capture is optional; the drag still follows the pointer.
  }
}

const releasePointer = (element: HTMLDivElement, pointerId: number) => {
  if (
    typeof element.hasPointerCapture !== 'function' ||
    !element.hasPointerCapture(pointerId)
  ) {
    return
  }

  element.releasePointerCapture(pointerId)
}

export function useMiniPdpImageCarousel(imageCount: number) {
  const viewportRef = useRef<HTMLDivElement>(null)
  const trackRef = useRef<HTMLDivElement>(null)
  const dragRef = useRef<DragSession>(idleDragSession())
  const indexRef = useRef(0)
  const maxIndexRef = useRef(0)

  const [currentIndex, setCurrentIndex] = useState(0)
  const [slideStep, setSlideStep] = useState(0)
  const [isDragging, setIsDragging] = useState(false)
  const [dragOffset, setDragOffset] = useState(0)

  const maxIndex = Math.max(0, imageCount - 1)

  indexRef.current = currentIndex
  maxIndexRef.current = maxIndex

  useEffect(() => {
    setCurrentIndex(0)
    setIsDragging(false)
    setDragOffset(0)
    dragRef.current = idleDragSession()
  }, [imageCount])

  useLayoutEffect(() => {
    const viewport = viewportRef.current

    if (!viewport) {
      return undefined
    }

    const measure = () => {
      setSlideStep(readSlideStep(trackRef.current))
    }

    measure()

    if (typeof ResizeObserver === 'undefined') {
      return undefined
    }

    const observer = new ResizeObserver(measure)

    observer.observe(viewport)

    return () => {
      observer.disconnect()
    }
  }, [imageCount])

  useEffect(() => {
    const viewport = viewportRef.current

    if (!viewport || maxIndex === 0) {
      return undefined
    }

    let locked = false
    let unlockTimer = 0

    const handleWheel = (event: WheelEvent) => {
      const isHorizontal = Math.abs(event.deltaX) > Math.abs(event.deltaY)

      if (!isHorizontal || Math.abs(event.deltaX) < DRAG_SENSITIVITY_PX) {
        return
      }

      const direction = event.deltaX > 0 ? 1 : -1
      const nextIndex = indexRef.current + direction

      if (nextIndex < 0 || nextIndex > maxIndex) {
        return
      }

      event.preventDefault()

      if (locked) {
        return
      }

      locked = true
      setCurrentIndex(nextIndex)
      unlockTimer = window.setTimeout(() => {
        locked = false
      }, WHEEL_LOCK_MS)
    }

    viewport.addEventListener('wheel', handleWheel, { passive: false })

    return () => {
      window.clearTimeout(unlockTimer)
      viewport.removeEventListener('wheel', handleWheel)
    }
  }, [maxIndex])

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    const isSecondaryMouseButton =
      event.pointerType === 'mouse' && event.button !== 0

    if (maxIndexRef.current === 0 || isSecondaryMouseButton) {
      return
    }

    dragRef.current = {
      active: true,
      pointerId: event.pointerId,
      startX: event.clientX,
    }
    setIsDragging(true)
    setDragOffset(0)

    if (event.pointerType !== 'mouse') {
      return
    }

    event.preventDefault()
    capturePointer(event.currentTarget, event.pointerId)
  }

  const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current

    if (!drag.active || event.pointerId !== drag.pointerId) {
      return
    }

    if (event.pointerType === 'mouse') {
      event.preventDefault()
    }

    setDragOffset(event.clientX - drag.startX)
  }

  const endDrag = (event: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current

    if (!drag.active || event.pointerId !== drag.pointerId) {
      return
    }

    const delta = event.clientX - drag.startX
    const lastIndex = maxIndexRef.current

    dragRef.current = idleDragSession()
    setIsDragging(false)
    setDragOffset(0)

    if (delta <= -DRAG_SENSITIVITY_PX) {
      setCurrentIndex((index) => Math.min(index + 1, lastIndex))
    } else if (delta >= DRAG_SENSITIVITY_PX) {
      setCurrentIndex((index) => Math.max(index - 1, 0))
    }

    releasePointer(event.currentTarget, event.pointerId)
  }

  return {
    viewportRef,
    trackRef,
    currentIndex,
    trackStyle: {
      transform: `translateX(${getTrackOffset({
        currentIndex,
        slideStep,
        maxIndex,
        dragOffset,
        isDragging,
      })}px)`,
      transition: isDragging ? 'none' : SLIDE_TRANSITION,
    },
    handlePointerDown,
    handlePointerMove,
    endDrag,
  }
}
